'use client';

import { useCallback, useSyncExternalStore } from 'react';

/**
 * The visitor's saved ranges, kept in this browser only.
 *
 * <p>Saving a range is a shortlist, not an account feature: it works for an
 * anonymous visitor, costs no request, and is read back by the header's saved
 * icon and the footer's saved-designs summary. Designs saved inside the online
 * planner live in the planner (PLAN.md, Contract 4); this list only remembers
 * which ranges caught the visitor's eye on the shop.
 *
 * <p>Built on `useSyncExternalStore` with an EMPTY server snapshot, so the
 * server render and the first client render agree and hydration never
 * mismatches. Storage that throws (private mode, blocked cookies) degrades to
 * an in-memory list for the session.
 */
export const SAVED_STORAGE_KEY = 'spatial-kitchen:saved';
const CHANGE_EVENT = 'spatial-kitchen:saved-change';

export interface SavedItem {
  id: string;
  name: string;
  href?: string;
}

const EMPTY: SavedItem[] = [];
let memory: SavedItem[] = EMPTY;
let cachedRaw: string | null | undefined;
let cachedList: SavedItem[] = EMPTY;

function read(): SavedItem[] {
  if (typeof window === 'undefined') return EMPTY;
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(SAVED_STORAGE_KEY);
  } catch {
    return memory;
  }
  if (raw === cachedRaw) return cachedList;
  cachedRaw = raw;
  try {
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    cachedList = Array.isArray(parsed)
      ? parsed.filter(
          (item): item is SavedItem =>
            !!item && typeof item === 'object' && typeof (item as SavedItem).id === 'string',
        )
      : EMPTY;
  } catch {
    cachedList = EMPTY;
  }
  return cachedList;
}

function write(list: SavedItem[]): void {
  memory = list;
  try {
    window.localStorage.setItem(SAVED_STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Storage unavailable: the in-memory copy above carries the session.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

function subscribe(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const onStorage = (event: StorageEvent) => {
    if (event.key === SAVED_STORAGE_KEY) onChange();
  };
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onStorage);
  };
}

const serverSnapshot = () => EMPTY;

export function useSavedItems(): {
  saved: SavedItem[];
  isSaved: (id: string) => boolean;
  toggle: (item: SavedItem) => void;
} {
  const saved = useSyncExternalStore(subscribe, read, serverSnapshot);
  const isSaved = useCallback((id: string) => saved.some((item) => item.id === id), [saved]);
  const toggle = useCallback((item: SavedItem) => {
    const current = read();
    const next = current.some((entry) => entry.id === item.id)
      ? current.filter((entry) => entry.id !== item.id)
      : [...current, item];
    write(next);
  }, []);
  return { saved, isSaved, toggle };
}
